import { useEffect, useRef, useState } from "react";
import { Camera, Mic, PencilLine, Square } from "lucide-react";
import { Drawer, useToast } from "@/components/system";
import {
  PrimaryButton,
  SecondaryButton,
  DialogFooter,
} from "@/components/forms";
import {
  createCapture,
  createPhotoCapture,
  createVoiceCapture,
} from "@/lib/capture/api";
import { VoiceRecorder } from "@/lib/capture/voice";
import { validatePhoto } from "@/lib/capture/photo";
import { formatError } from "@/lib/format-error";

/**
 * HOTFIX-BW19.4 PR-A Bug 1: unified Capture sheet (Text / Voice / Photo).
 *
 * Replaces the BW19 PR-B trio of separate buttons (text modal, mic button,
 * photo button) that cluttered /today. One sheet, three tabs. Text is the
 * default tab so the n shortcut keeps the keyboard-first flow.
 */

type TabKey = "text" | "voice" | "photo";

export function CaptureSheet({
  open,
  onClose,
  initialTab = "text",
}: {
  open: boolean;
  onClose: () => void;
  initialTab?: TabKey;
}) {
  const [tab, setTab] = useState<TabKey>(initialTab);

  useEffect(() => {
    if (open) setTab(initialTab);
  }, [open, initialTab]);

  return (
    <Drawer open={open} onClose={onClose} title="Capture" size="md">
      <TabBar tab={tab} onChange={setTab} />
      {tab === "text" && <TextTab open={open && tab === "text"} onClose={onClose} />}
      {tab === "voice" && <VoiceTab onClose={onClose} />}
      {tab === "photo" && <PhotoTab onClose={onClose} />}
    </Drawer>
  );
}

function TabBar({
  tab,
  onChange,
}: {
  tab: TabKey;
  onChange: (k: TabKey) => void;
}) {
  const tabs: Array<{ key: TabKey; label: string; Icon: typeof Mic }> = [
    { key: "text", label: "Text", Icon: PencilLine },
    { key: "voice", label: "Voice", Icon: Mic },
    { key: "photo", label: "Photo", Icon: Camera },
  ];
  return (
    <div
      role="tablist"
      style={{
        display: "grid",
        gridTemplateColumns: `repeat(${tabs.length}, minmax(0, 1fr))`,
        gap: 4,
        background: "var(--bg-3)",
        borderRadius: "var(--radius-card)",
        padding: 4,
        marginBottom: "var(--space-3)",
      }}
    >
      {tabs.map((t) => {
        const active = tab === t.key;
        return (
          <button
            key={t.key}
            role="tab"
            aria-selected={active}
            type="button"
            onClick={() => onChange(t.key)}
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 6,
              padding: "8px 8px",
              minWidth: 0,
              background: active ? "var(--bg)" : "transparent",
              border: "none",
              borderRadius: "var(--radius-chip)",
              color: active ? "var(--fg)" : "var(--fg-3)",
              fontSize: "var(--type-body-s)",
              cursor: "pointer",
              fontWeight: active ? 500 : 400,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            <t.Icon size={14} strokeWidth={1.6} />
            {t.label}
          </button>
        );
      })}
    </div>
  );
}

function TextTab({ open, onClose }: { open: boolean; onClose: () => void }) {
  const toast = useToast();
  const [content, setContent] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (open) {
      setContent("");
      setSubmitting(false);
      const t = window.setTimeout(() => textareaRef.current?.focus(), 60);
      return () => window.clearTimeout(t);
    }
  }, [open]);

  async function save() {
    const trimmed = content.trim();
    if (!trimmed) return;
    setSubmitting(true);
    onClose();
    try {
      await createCapture({ content: trimmed, source: "keyboard" });
      toast.show("Captured. Triage in a few minutes.");
    } catch (err) {
      toast.show(`Capture failed: ${formatError(err)}`);
    }
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
      e.preventDefault();
      void save();
    }
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
      <p style={{ margin: 0, color: "var(--fg-2)", fontSize: 13 }}>
        State: anything in your head you don't want to keep tracking.
        <br />
        Meaning: writing it down frees the bandwidth that competes with sleep.
        <br />
        Next move: type and press Cmd+Enter.
      </p>
      <textarea
        ref={textareaRef}
        value={content}
        onChange={(e) => setContent(e.target.value)}
        onKeyDown={onKeyDown}
        placeholder="Reply to Maria, book Berlin train, finalize lecture slides."
        rows={6}
        maxLength={5000}
        style={{
          width: "100%",
          padding: "var(--space-3)",
          background: "var(--bg)",
          color: "var(--fg)",
          border: "1px solid var(--hair)",
          borderRadius: "var(--radius-card)",
          fontFamily: "var(--font-sans)",
          fontSize: 15,
          resize: "vertical",
          minHeight: 140,
        }}
      />
      <DialogFooter>
        <SecondaryButton onClick={onClose} disabled={submitting}>
          Cancel
        </SecondaryButton>
        <PrimaryButton
          onClick={() => void save()}
          disabled={submitting || content.trim().length === 0}
        >
          Capture
        </PrimaryButton>
      </DialogFooter>
    </div>
  );
}

type VoicePhase = "idle" | "recording" | "uploading";

function VoiceTab({ onClose }: { onClose: () => void }) {
  const toast = useToast();
  const [phase, setPhase] = useState<VoicePhase>("idle");
  const recorderRef = useRef<VoiceRecorder | null>(null);
  const tickerRef = useRef<number | null>(null);
  const [seconds, setSeconds] = useState(0);

  useEffect(() => {
    if (phase === "recording") {
      const start = Date.now();
      tickerRef.current = window.setInterval(() => {
        setSeconds(Math.floor((Date.now() - start) / 1000));
      }, 250);
    } else if (tickerRef.current != null) {
      window.clearInterval(tickerRef.current);
      tickerRef.current = null;
      setSeconds(0);
    }
    return () => {
      if (tickerRef.current != null) {
        window.clearInterval(tickerRef.current);
        tickerRef.current = null;
      }
    };
  }, [phase]);

  useEffect(() => {
    return () => {
      const r = recorderRef.current;
      if (r) void r.stop().catch(() => {});
      recorderRef.current = null;
    };
  }, []);

  async function start() {
    try {
      const r = new VoiceRecorder();
      await r.start();
      recorderRef.current = r;
      setPhase("recording");
    } catch (err) {
      toast.show(`Mic unavailable: ${formatError(err)}`);
      recorderRef.current = null;
    }
  }

  async function stopAndUpload() {
    const r = recorderRef.current;
    if (!r) return;
    setPhase("uploading");
    onClose();
    try {
      const recording = await r.stop();
      const result = await createVoiceCapture({
        audio: recording.blob,
        durationS: recording.durationS,
        filename: `voice-${Date.now()}.${guessExt(recording.mime)}`,
      });
      toast.show(
        result.transcription
          ? `Captured: ${preview(result.transcription)}`
          : "Captured. Triage in a few minutes.",
      );
    } catch (err) {
      toast.show(`Voice capture failed: ${formatError(err)}`);
    } finally {
      recorderRef.current = null;
      setPhase("idle");
    }
  }

  const Icon = phase === "recording" ? Square : Mic;
  const primaryLabel =
    phase === "recording"
      ? `Stop and save (${seconds}s)`
      : phase === "uploading"
        ? "Uploading..."
        : "Start recording";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
      <p style={{ margin: 0, color: "var(--fg-2)", fontSize: 13 }}>
        State: hands busy, thoughts faster than typing.
        <br />
        Meaning: voice keeps the capture velocity high.
        <br />
        Next move: tap record, speak, tap stop.
      </p>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "var(--space-6) var(--space-3)",
          background: "var(--bg)",
          border: "1px solid var(--hair)",
          borderRadius: "var(--radius-card)",
          minHeight: 140,
        }}
      >
        <button
          type="button"
          onClick={() => (phase === "idle" ? void start() : void stopAndUpload())}
          disabled={phase === "uploading"}
          aria-label={primaryLabel}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            padding: "12px 18px",
            background:
              phase === "recording" ? "var(--danger, #c53030)" : "var(--accent, var(--fg))",
            color: phase === "recording" ? "white" : "var(--bg)",
            border: "none",
            borderRadius: "var(--radius-card)",
            fontFamily: "var(--font-sans)",
            fontSize: 15,
            fontWeight: 500,
            cursor: phase === "uploading" ? "wait" : "pointer",
          }}
        >
          <Icon size={18} />
          <span>{primaryLabel}</span>
        </button>
      </div>
      <DialogFooter>
        <SecondaryButton onClick={onClose} disabled={phase !== "idle"}>
          Cancel
        </SecondaryButton>
      </DialogFooter>
    </div>
  );
}

function PhotoTab({ onClose }: { onClose: () => void }) {
  const toast = useToast();
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);

  async function onChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    const validation = validatePhoto(file);
    if (!validation.ok) {
      toast.show(validation.reason);
      return;
    }

    setBusy(true);
    onClose();
    try {
      const result = await createPhotoCapture({ image: file, filename: file.name });
      toast.show(
        result.ocr_text
          ? `Captured: ${preview(result.ocr_text)}`
          : "Captured. Triage in a few minutes.",
      );
    } catch (err) {
      toast.show(`Photo capture failed: ${formatError(err)}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
      <p style={{ margin: 0, color: "var(--fg-2)", fontSize: 13 }}>
        State: a whiteboard, a receipt, a scribbled list.
        <br />
        Meaning: capture the image, let the worker OCR it.
        <br />
        Next move: tap to open the camera or pick a file.
      </p>
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={busy}
        aria-label="Capture photo"
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 8,
          padding: "var(--space-6) var(--space-3)",
          background: "var(--bg)",
          color: "var(--fg)",
          border: "1px dashed var(--hair)",
          borderRadius: "var(--radius-card)",
          fontFamily: "var(--font-sans)",
          fontSize: 15,
          minHeight: 140,
          cursor: busy ? "wait" : "pointer",
        }}
      >
        <Camera size={20} />
        <span>{busy ? "Reading..." : "Take photo or choose file"}</span>
      </button>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={onChange}
        style={{ display: "none" }}
      />
      <DialogFooter>
        <SecondaryButton onClick={onClose} disabled={busy}>
          Cancel
        </SecondaryButton>
      </DialogFooter>
    </div>
  );
}

function preview(s: string): string {
  const oneLine = s.replace(/\s+/g, " ").trim();
  return oneLine.length <= 60 ? oneLine : `${oneLine.slice(0, 57)}...`;
}

function guessExt(mime: string): string {
  if (mime.includes("mp4")) return "m4a";
  if (mime.includes("mpeg")) return "mp3";
  if (mime.includes("ogg")) return "ogg";
  return "webm";
}
